import * as React from 'react';
import { Spinner } from 'components/Common/Spinner';
import FadeComponent from 'components/Common/FadeComponent';
import { DataState } from 'typings/enums';
import './statusIndicator.scss';

interface StatusIndicatorProps {
    dataState: DataState;
    logout: () => void;
}

interface StatusIndicatorState {
    show: boolean;        // whether to show the indicator
    message: string;      // message to display
    details: string;      // extra details to accompany message
    error: boolean;       // whether the message/state should display as an error
}

class StatusIndicator extends React.PureComponent<StatusIndicatorProps> {
    private static _showDelay: number = 200;    // in milliseconds

    constructor(props: StatusIndicatorProps) {
        super(props);
    }

    // Helper which returns whether we should show status feedback at all for the current state
    public static _shouldShowForState(dataState: DataState): boolean {
        return dataState !== DataState.Idle;
    }

    render(): React.ReactNode {
        const { dataState, logout } = this.props;
        const { show, message, error, details } = this._computeNextState(dataState ?? DataState.Idle);

        // if showing indicator and we're displaying progress, don't fade in immediately
        // to avoid flicker if transition is fast
        const showOnDelay = show && StatusIndicator._shouldShowOnDelay(dataState);
        const fadeAnimationDelay = showOnDelay ? StatusIndicator._showDelay : 0;

        return (
            <FadeComponent
                className="status-overlay"
                show={show}
                fadeDelay={fadeAnimationDelay}
            >
                <div className="status">
                    <div className={`message${error ? ' error' : ''}`}>
                        {error && <i className="icon-error-alt" />}
                        {!error && <Spinner />}
                        {message}
                    </div>
                    {details && <div className="details">{details}</div>}
                    {error && <div className="logout"><button className="pxl-link" onClick={logout}>Logout</button></div>}
                </div>
            </FadeComponent>
        );
    }

    // Whether or not the current state should be considered/rendered as an error
    private static _isErrorState(dataState: DataState): boolean {
        return dataState === DataState.Timeout || dataState === DataState.Error;
    }

    // Returns the message text to display for the specified state
    private static _getMessageForState(dataState: DataState): string {
        if (dataState === DataState.Pending) {
            return 'Loading...';
        } else if (dataState === DataState.Timeout) {
            return 'Request timed out';
        } else if (dataState === DataState.Error) {
            return 'An error occurred';
        }

        return '';
    }

    private static _getDetailsForState(dataState: DataState): string {
        if (dataState === DataState.Timeout || dataState === DataState.Error) {
            return 'Please reload the page and try again or';
        }

        return '';
    }

    // Presuming we should show feedback for this state, returns whether the feedback
    // should be delayed, vs shown immediately
    private static _shouldShowOnDelay(dataState: DataState): boolean {
        return dataState === DataState.Pending;
    }

    private _computeNextState(dataState: DataState): StatusIndicatorState {
        const shouldShow = StatusIndicator._shouldShowForState(dataState);

        if (!shouldShow) {
            // if hiding, preserve message and details (vs update based on new state)
            return { show: false } as StatusIndicatorState;
        }

        return {
            show: true,
            message: StatusIndicator._getMessageForState(dataState),
            details: StatusIndicator._getDetailsForState(dataState),
            error: StatusIndicator._isErrorState(dataState)
        };
    }
}

export { StatusIndicator };
export default StatusIndicator;